import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { ChatMessage } from "./ChatMessage";
import { QuickReplies } from "./QuickReplies";
import { TypingIndicator } from "./TypingIndicator";
import { useChat, ChatMessage as ChatMessageType } from "@/hooks/useChat";
import { isSoundEnabled, setSoundEnabled } from "@/lib/notificationSound";
import { Send, Loader2, X, RotateCcw, MessageSquareOff, Volume2, VolumeX } from "lucide-react";
import { createId } from "@/lib/createId";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ChatInterfaceProps {
  onClose: () => void;
}

export function ChatInterface({ onClose }: ChatInterfaceProps) {
  const [input, setInput] = useState("");
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactName, setContactName] = useState("");
  const [isEnding, setIsEnding] = useState(false);
  const [connectionState, setConnectionState] = useState<"connecting" | "connected" | "failed">("connecting");
  const [soundEnabled, setSoundEnabledState] = useState(isSoundEnabled);
  const [thankYouMessage, setThankYouMessage] = useState<ChatMessageType | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasStartedRef = useRef(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const {
    messages,
    isLoading,
    isRestoring,
    conversationId,
    conversationData,
    startConversation,
    restoreConversation,
    sendMessage,
    updateConversationData,
    endConversation,
  } = useChat();

  // Start or restore conversation on mount
  useEffect(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      setConnectionState("connecting");
      
      // Try to restore first, then start new if no existing conversation
      restoreConversation().then((restoredId) => {
        if (restoredId) {
          setConnectionState("connected");
        } else {
          return startConversation().then((id) => {
            setConnectionState(id ? "connected" : "failed");
          });
        }
      });
    }
  }, [restoreConversation, startConversation]);

  // Update connection state when conversationId changes
  useEffect(() => {
    if (conversationId) {
      setConnectionState("connected");
    }
  }, [conversationId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, isLoading, thankYouMessage]);

  // Handle scroll to hide the scroll hint
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop > 20) {
      setShowScrollHint(false);
    }
  }, []);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabledState(newValue);
    setSoundEnabled(newValue);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    sendMessage(trimmed);
    setInput("");
  };

  const handleQuickReply = (message: string) => {
    if (isLoading) return;
    sendMessage(message);
  };

  const handleClose = async () => {
    if (messages.length > 1) {
      await endConversation();
    }
    onClose();
  };

  const handleRetryConnection = async () => {
    setConnectionState("connecting");
    hasStartedRef.current = false;
    const id = await startConversation();
    setConnectionState(id ? "connected" : "failed");
  };

  const handleNewConversation = async () => {
    if (messages.length > 1) {
      await endConversation();
    }
    setThankYouMessage(null);
    setIsEnding(false);
    setContactEmail("");
    setContactPhone("");
    setContactName("");
    setConnectionState("connecting");
    const id = await startConversation();
    setConnectionState(id ? "connected" : "failed");
  };

  // Check if we have contact info
  const hasContactInfo = !!(conversationData.user_email || conversationData.user_phone);

  // Handle End Chat button click
  const handleEndChatClick = () => {
    if (hasContactInfo) {
      // Already have contact info, show confirmation directly
      setShowEndDialog(true);
    } else {
      // Need to collect contact info first
      setShowContactDialog(true);
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async () => {
    // Save the contact info
    if (contactEmail || contactPhone || contactName) {
      await updateConversationData({
        user_email: contactEmail || undefined,
        user_phone: contactPhone || undefined,
        user_name: contactName || undefined,
      });
    }
    setShowContactDialog(false);
    setShowEndDialog(true);
  };

  // Skip contact collection
  const handleSkipContact = () => {
    setShowContactDialog(false);
    setShowEndDialog(true);
  };

  const handleEndConversation = async () => {
    setShowEndDialog(false);
    setIsEnding(true);
    
    // Add thank-you message
    const thankYou: ChatMessageType = {
      id: createId(),
      role: "assistant",
      content: "Thanks for chatting with us! ✨ A summary of our conversation has been sent to our team, and we'll be in touch soon. Feel free to start a new conversation anytime!",
      created_at: new Date().toISOString(),
    };
    setThankYouMessage(thankYou);
    
    // Wait for user to read the message, then end and close
    setTimeout(async () => {
      await endConversation();
      onClose();
    }, 2500);
  };

  // Combine messages with thank-you message for display
  const displayMessages = thankYouMessage 
    ? [...messages, thankYouMessage] 
    : messages;

  // Show quick replies only after the latest assistant message (not during loading/ending/booking shown)
  const lastMessage = displayMessages[displayMessages.length - 1];
  const showQuickReplies =
    !isLoading &&
    !isEnding &&
    lastMessage?.role === "assistant" &&
    !lastMessage?.content.includes("[SHOW_BOOKING]");

  const isContactFormValid = contactEmail.trim() || contactPhone.trim();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between border-b border-border bg-background px-4 py-3">
        <div>
          <h3 className="font-serif text-lg font-semibold text-foreground">
            Pixie Clicks Assistant
          </h3>
          <p className="text-xs text-muted-foreground">
            Wedding photography & videography
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSound}
            className="h-8 w-8"
            title={soundEnabled ? "Mute sounds" : "Unmute sounds"}
          >
            {soundEnabled ? (
              <Volume2 className="h-4 w-4" />
            ) : (
              <VolumeX className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNewConversation}
            className="h-8 w-8"
            title="New conversation"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full" type="always" onScrollCapture={handleScroll}>
          <div className="p-4 space-y-4">
            {connectionState === "connecting" && messages.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Connecting...</span>
                </div>
              </div>
            )}
            {connectionState === "failed" && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <p className="text-sm text-muted-foreground text-center">
                  Couldn't connect. Please try again.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryConnection}
                >
                  <RotateCcw className="mr-2 h-3 w-3" />
                  Retry
                </Button>
              </div>
            )}
            {displayMessages.map((message) => (
              <ChatMessage
                key={message.id}
                role={message.role}
                content={message.content}
              />
            ))}
            {isLoading && messages[messages.length - 1]?.role === "user" && (
              <TypingIndicator />
            )}
            {showQuickReplies && connectionState === "connected" && (
              <QuickReplies 
                onSelect={handleQuickReply} 
                disabled={isLoading}
                conversationData={conversationData}
                messages={messages}
                conversationId={conversationId}
              />
            )}
            <div ref={scrollAnchorRef} />
          </div>
        </ScrollArea>
        
        {/* Scroll hint gradient */}
        <div 
          className={`absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none transition-opacity duration-300 ${
            showScrollHint && messages.length > 2 ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex-shrink-0 border-t border-border bg-background p-4"
      >
        <div className="flex items-center gap-2">
          {messages.length > 1 && !isEnding && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleEndChatClick}
              className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
            >
              <MessageSquareOff className="mr-1 h-3 w-3" />
              End Chat
            </Button>
          )}
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              isEnding 
                ? "Conversation ended" 
                : connectionState !== "connected" 
                  ? "Connecting..." 
                  : "Type your message..."
            }
            disabled={isLoading || isEnding || connectionState !== "connected"}
            className="flex-1"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || isLoading || isEnding}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {/* Contact Collection Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Stay in touch!</DialogTitle>
            <DialogDescription>
              Leave your contact info so we can follow up with you about your wedding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Your name</Label>
              <Input
                id="contact-name"
                placeholder="Jane & John"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                placeholder="you@example.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Phone (optional)</Label>
              <Input
                id="contact-phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkipContact}
              className="text-muted-foreground"
            >
              Skip
            </Button>
            <Button
              type="button"
              onClick={handleContactSubmit}
              disabled={!isContactFormValid}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Conversation Confirmation Dialog */}
      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll send a summary of our conversation to our team so they can follow up with you. You can start a new conversation anytime!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Chatting</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndConversation}>
              End & Send Summary
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
